import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { SEO } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { CONTACT } from '../../constants/images';

export const Miscellaneous = () => {
  const services = [
    {
      title: 'PICC Line Insertions',
      description: 'A PICC (Peripherally Inserted Central Catheter) is a long, thin catheter inserted through a vein in the upper arm and advanced to a large central vein near the heart — providing reliable, long-term IV access without repeated peripheral needle sticks.',
      candidates: ['Cancer patients requiring prolonged chemotherapy', 'Patients on long-term IV antibiotics', 'Patients requiring parenteral nutrition', 'Patients needing frequent blood draws or infusions where peripheral IV access has failed'],
      treatments: ['Insertion under ultrasound guidance', 'Fluoroscopy to confirm tip position', 'Precision and minimal discomfort', 'Reduced misplacement and infection complications']
    },
    {
      title: 'Permcath / Tunnelled Dialysis Catheter',
      description: 'A tunnelled dialysis catheter (permcath) is a long-term central venous catheter placed for patients who need haemodialysis but do not have a functioning AV fistula. Tunnelled under the skin to reduce infection risk.',
      candidates: ['Patients needing urgent dialysis', 'No functioning AV fistula', 'Bridge to fistula maturation'],
      treatments: ['Ultrasound and fluoroscopy guided insertion', 'Via internal jugular vein in neck', 'Tunnelled under chest skin', 'Available at Bombay Hospital and other Mumbai locations']
    },
    {
      title: 'Varicocele Embolization',
      description: 'A varicocele is an abnormal enlargement of the veins within the scrotum — similar to varicose veins. Present in approximately 15% of men and up to 35-40% of men investigated for male infertility. They raise testicular temperature and impair sperm production.',
      candidates: ['Men with dull aching scrotal discomfort', 'Visible or palpable enlarged scrotal veins', 'Poor semen analysis (low count, poor motility)', 'Testicular atrophy in longstanding cases'],
      treatments: ['Catheter through small groin/neck puncture', 'Embolic coils block dilated veins', 'No surgical incision, no general anaesthesia', 'Day-care procedure, recovery within 2-3 days', 'Lower hydrocele risk than surgical varicocelectomy']
    },
    {
      title: 'Arteriovenous & Venous Malformation Management',
      description: 'Vascular malformations are abnormal clusters of blood vessels — present from birth, appearing clinically in childhood or adulthood. They cause pain, swelling, skin discolouration, functional impairment, or cosmetic concern.',
      candidates: ['Patients with venous malformations (VMs)', 'Patients with arteriovenous malformations (AVMs)', 'Multiple sessions often required'],
      treatments: ['Sclerotherapy for venous malformations', 'Superselective embolization for AVMs', 'Image-guided precision', 'Often combined with surgical excision for complex AVMs']
    }
  ];

  const faqs = [
    { q: 'How long does a PICC line last in Mumbai?', a: 'A properly placed PICC line can remain functional for weeks to months with appropriate care. Dr. Punit inserts PICC lines under imaging guidance, reducing complications.' },
    { q: 'Can varicocele embolization improve fertility?', a: 'Yes. Studies show varicocele treatment improves sperm count, motility, and morphology in a significant proportion of men, and improves pregnancy rates in infertile couples.' },
    { q: 'Is varicocele embolization better than varicocele surgery?', a: 'Both have comparable outcomes. Embolization advantages: no incision, no general anaesthesia, faster recovery, and lower risk of hydrocele formation.' },
    { q: 'Can vascular malformations be fully cured with embolization?', a: 'Outcomes depend on the type, size, and location. Many venous malformations can be controlled or eliminated with sclerotherapy. AVMs typically require multiple sessions.' }
  ];

  return (
    <>
      <SEO 
        title="PICC Line, Varicocele & Vascular Malformation Treatment in Mumbai | Dr. Punit Mahajan"
        description="PICC line & permcath insertions, varicocele embolization & vascular malformation management in Mumbai. Minimally invasive, image-guided procedures by Dr. Punit Mahajan."
      />

      <section className="bg-gradient-to-br from-blue-950 to-slate-900 text-white py-20 relative overflow-hidden"><div className="absolute inset-0 pattern-hero opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-text-reveal">Specialised Interventional Procedures</h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto animate-text-reveal" style={{ animationDelay: "300ms" }}>Precision for Every Need — Mumbai</p>
        </div>
      </section>

      <section className="py-16 bg-cream-textured relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose max-w-none mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Introduction</h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Beyond his core vascular and organ-specific IR practice, Dr. Punit Mahajan performs a range of specialised image-guided procedures in Mumbai — from long-term IV access for cancer patients to varicocele embolization for male infertility.
            </p>
          </div>

          <div className="space-y-8">
            {services.map((service, index) => (
              <Card key={index} className="card-gold-hover transition-all bg-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">{service.title}</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Who Needs This:</h4>
                      <ul className="space-y-2">
                        {service.candidates.map((c, idx) => (
                          <li key={idx} className="flex items-start"><CheckCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-slate-600">{c}</span></li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Procedure Details:</h4>
                      <ul className="space-y-2">
                        {service.treatments.map((t, idx) => (
                          <li key={idx} className="flex items-start"><ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-slate-600">{t}</span></li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-cream-stripes relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <Card key={idx} className="border-l-4 border-amber-500 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-blue-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold">Specialised Care for Specific Needs</h2>
          <p className="text-xl text-slate-300">Discuss your case with Dr. Punit Mahajan</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${CONTACT.phone}`}><Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">Call {CONTACT.phone}</Button></a>
            <Link to="/contact"><Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">Book Consultation</Button></Link>
          </div>
        </div>
      </section>
    </>
  );
};
